// ============================================================================
// THE CYCLE — Momentum Board
// 30-day CVR momentum: 4 quadrants + scatter chart. Replaces CVR Movers
// and Hot/Cold Streaks with a unified, insight-driven surface.
// ============================================================================
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Avatar, Chip, Skeleton, Grid,
  useTheme, alpha, ToggleButton, ToggleButtonGroup,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, InputAdornment, Tooltip, useMediaQuery,
} from '@mui/material';
import {
  Whatshot, AcUnit, TrendingUp, TrendingDown, ShowChart, Search,
} from '@mui/icons-material';
import {
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, ResponsiveContainer, ReferenceLine, Cell,
} from 'recharts';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { statsApi } from '../services/apiService';
import { getTeamLogoUrl } from '../utils/teamLogos';
import { makePlayerSlug } from '../utils/playerSlug';
import { useYear } from '../context/YearContext';
import PageYearSelector from '../components/common/PageYearSelector';

// ─── constants ───────────────────────────────────────────────────────────────

const TH = { fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 };

// Quadrant definitions (X = rollingCVR, Y = cvrDelta)
const QUADRANTS = [
  {
    id: 'rising',
    label: 'Rising Stars',
    sub: 'High CVR + gaining momentum',
    icon: <TrendingUp />,
    color: '#2563eb',
    test: (cvr, delta) => cvr >= 65 && delta > 0,
    action: 'BUY',
    actionColor: '#16a34a',
  },
  {
    id: 'heating',
    label: 'Heating Up',
    sub: 'Breaking out — surging fast',
    icon: <Whatshot />,
    color: '#16a34a',
    test: (cvr, delta) => cvr < 65 && delta >= 7,
    action: 'TARGET',
    actionColor: '#0891b2',
  },
  {
    id: 'declining',
    label: 'Declining Stars',
    sub: 'Good players losing form',
    icon: <TrendingDown />,
    color: '#ea580c',
    test: (cvr, delta) => cvr >= 65 && delta <= -5,
    action: 'FADE',
    actionColor: '#ea580c',
  },
  {
    id: 'cold',
    label: 'Cold Spells',
    sub: 'Below average and sliding',
    icon: <AcUnit />,
    color: '#dc2626',
    test: (cvr, delta) => cvr < 65 && delta < 0,
    action: 'AVOID',
    actionColor: '#dc2626',
  },
];

const getQuadrant = (rollingCVR, delta) => {
  for (const q of QUADRANTS) {
    if (q.test(rollingCVR, delta)) return q;
  }
  // Fallback: "Stable" players not fitting a quadrant (cvr < 65, 0 < delta < 7)
  return null;
};

const cvrColor = (cvr) => {
  if (cvr >= 80) return '#16a34a';
  if (cvr >= 65) return '#0891b2';
  if (cvr >= 50) return '#ca8a04';
  if (cvr >= 35) return '#ea580c';
  return '#dc2626';
};

// Momentum score: quality × momentum — highest = buy signal
const momentumScore = (rollingCVR, delta) => {
  const q = Math.max(0, rollingCVR);
  const d = delta;
  return Math.round(q * 0.6 + d * 2);
};

// ─── Custom scatter tooltip ───────────────────────────────────────────────────

const ScatterTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0]?.payload;
  if (!p) return null;
  return (
    <Box sx={{
      bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
      borderRadius: 2, p: 1.5, boxShadow: 4, minWidth: 160,
    }}>
      <Typography variant="body2" fontWeight={700}>{p.name}</Typography>
      <Typography variant="caption" color="text.secondary">{p.team} · {p.position}</Typography>
      <Box sx={{ mt: 0.5, display: 'flex', gap: 1.5 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">CVR Now</Typography>
          <Typography variant="body2" fontWeight={700} sx={{ color: cvrColor(p.rollingCVR) }}>{p.rollingCVR}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">30d Δ</Typography>
          <Typography variant="body2" fontWeight={700} sx={{ color: p.cvrDelta >= 0 ? '#16a34a' : '#dc2626' }}>
            {p.cvrDelta >= 0 ? '+' : ''}{p.cvrDelta}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

// ─── Quadrant card ────────────────────────────────────────────────────────────

const QuadrantCard = ({ q, players, navigate, theme }) => (
  <Card elevation={0} sx={{
    borderRadius: 3, height: '100%',
    border: `1px solid ${alpha(q.color, 0.3)}`,
    bgcolor: alpha(q.color, 0.03),
  }}>
    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Box sx={{
          width: 32, height: 32, borderRadius: 1.5,
          bgcolor: alpha(q.color, 0.12), color: q.color,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {React.cloneElement(q.icon, { sx: { fontSize: 18 } })}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" fontWeight={800} sx={{ color: q.color, lineHeight: 1.1 }}>
            {q.label}
          </Typography>
          <Typography variant="caption" color="text.secondary">{q.sub}</Typography>
        </Box>
        <Chip label={q.action} size="small" sx={{
          height: 18, fontSize: '0.62rem', fontWeight: 800,
          bgcolor: alpha(q.actionColor, 0.12), color: q.actionColor,
        }} />
      </Box>

      {players.length === 0
        ? <Typography variant="caption" color="text.disabled" sx={{ pl: 0.5 }}>No players in this zone</Typography>
        : players.slice(0, 6).map((p, i) => (
            <Box
              key={p.compositeId}
              onClick={() => navigate(`/players/profile/${makePlayerSlug(p.name)}`)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1, py: 0.6,
                cursor: 'pointer', borderRadius: 1,
                '&:hover': { bgcolor: alpha(theme.palette.action.hover, 0.5) },
                ...(i < players.length - 1 && { borderBottom: `1px solid ${alpha(theme.palette.divider, 0.4)}` }),
              }}
            >
              <Avatar src={getTeamLogoUrl(p.team)} sx={{ width: 20, height: 20 }} variant="rounded" />
              <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }} noWrap>{p.name}</Typography>
              <Chip
                label={`CVR ${p.rollingCVR}`} size="small"
                sx={{ height: 16, fontSize: '0.62rem', fontWeight: 700, bgcolor: alpha(cvrColor(p.rollingCVR), 0.1), color: cvrColor(p.rollingCVR) }}
              />
              <Typography variant="caption" fontWeight={700} sx={{ color: p.cvrDelta >= 0 ? '#16a34a' : '#dc2626', minWidth: 32, textAlign: 'right' }}>
                {p.cvrDelta >= 0 ? '+' : ''}{p.cvrDelta}
              </Typography>
            </Box>
          ))}
    </CardContent>
  </Card>
);

// ─── Full list row ────────────────────────────────────────────────────────────

const MoverRow = ({ rank, player: p, navigate, theme }) => {
  const q = getQuadrant(p.rollingCVR, p.cvrDelta);
  return (
    <TableRow
      component={motion.tr}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: Math.min(rank * 0.010, 0.3) }}
      hover
      sx={{ cursor: 'pointer', '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) } }}
      onClick={() => navigate(`/players/profile/${makePlayerSlug(p.name)}`)}
    >
      <TableCell sx={{ width: 36, pl: 1.5 }}>
        <Typography variant="body2" color="text.secondary">{rank}</Typography>
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar src={getTeamLogoUrl(p.team)} sx={{ width: 22, height: 22 }} variant="rounded" />
          <Box>
            <Typography variant="body2" fontWeight={600} lineHeight={1.1}>{p.name}</Typography>
            <Typography variant="caption" color="text.secondary">{p.team} · {p.position}</Typography>
          </Box>
        </Box>
      </TableCell>
      <TableCell align="center">
        <Chip label={`CVR ${p.rollingCVR}`} size="small" sx={{
          height: 20, fontSize: '0.72rem', fontWeight: 700,
          bgcolor: alpha(cvrColor(p.rollingCVR), 0.12), color: cvrColor(p.rollingCVR),
        }} />
      </TableCell>
      <TableCell align="center">
        <Typography variant="body2" fontWeight={700} sx={{ color: p.cvrDelta >= 0 ? '#16a34a' : '#dc2626' }}>
          {p.cvrDelta >= 0 ? '+' : ''}{p.cvrDelta}
        </Typography>
      </TableCell>
      <TableCell align="center">
        <Typography variant="body2" color="text.secondary">{p.baselineCVR}</Typography>
      </TableCell>
      <TableCell align="center">
        <Typography variant="body2" fontWeight={600}>{p.momentum}</Typography>
      </TableCell>
      {q && (
        <TableCell align="center">
          <Chip label={q.label} size="small" sx={{
            height: 18, fontSize: '0.62rem', fontWeight: 700,
            bgcolor: alpha(q.color, 0.1), color: q.color,
          }} />
        </TableCell>
      )}
      {!q && <TableCell align="center"><Typography variant="caption" color="text.disabled">Stable</Typography></TableCell>}
    </TableRow>
  );
};

// ─── main component ───────────────────────────────────────────────────────────

const MomentumBoard = () => {
  const theme    = useTheme();
  const navigate = useNavigate();
  const { year } = useYear();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [playerType, setPlayerType] = useState('Batter');
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [risers, setRisers]         = useState([]);
  const [fallers, setFallers]       = useState([]);
  const [search, setSearch]         = useState('');
  const [meta, setMeta]             = useState(null);

  // ── fetch ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);

    statsApi.getCVRMovers({
      year,
      limit: 100,
      minGames: 8,
      minDelta: 0.01,    // effectively 0 — get all movers
      baseline: 'snapshot',
      days: 30,
    }).then(data => {
      if (!alive) return;
      setRisers(data?.risers  || []);
      setFallers(data?.fallers || []);
      setMeta(data?._meta || null);
      setLoading(false);
    }).catch(err => {
      if (!alive) return;
      setError(err?.message || 'Failed to load momentum data');
      setLoading(false);
    });

    return () => { alive = false; };
  }, [year]);

  // ── enrich with momentum score + filter by type ────────────────────────────
  const allPlayers = useMemo(() => {
    const combined = [...risers, ...fallers];
    return combined
      .filter(p => !playerType || p.playerType === playerType)
      .map(p => ({ ...p, momentum: momentumScore(p.rollingCVR, p.cvrDelta) }))
      .sort((a, b) => b.momentum - a.momentum);
  }, [risers, fallers, playerType]);

  // ── scatter data ───────────────────────────────────────────────────────────
  const scatterData = useMemo(() => allPlayers.map(p => ({
    x: p.rollingCVR,
    y: p.cvrDelta,
    name: p.name,
    team: p.team,
    position: p.position,
    rollingCVR: p.rollingCVR,
    cvrDelta: p.cvrDelta,
    momentum: p.momentum,
    quadrant: getQuadrant(p.rollingCVR, p.cvrDelta),
  })), [allPlayers]);

  // ── quadrant player lists ──────────────────────────────────────────────────
  const byQuadrant = useMemo(() => {
    const result = {};
    QUADRANTS.forEach(q => {
      result[q.id] = allPlayers
        .filter(p => q.test(p.rollingCVR, p.cvrDelta))
        .sort((a, b) => b.momentum - a.momentum);
    });
    return result;
  }, [allPlayers]);

  // ── search filter ──────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    if (!search) return allPlayers;
    const q = search.toLowerCase();
    return allPlayers.filter(p => p.name.toLowerCase().includes(q) || p.team.toLowerCase().includes(q));
  }, [allPlayers, search]);

  if (error) {
    return (
      <Box sx={{ p: 3, maxWidth: 700, mx: 'auto' }}>
        <Card elevation={0} sx={{ borderRadius: 3, p: 3 }}>
          <Typography color="error" fontWeight={600}>{error}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Momentum data requires rolling snapshots — check back tomorrow or try a different year.
          </Typography>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1100, mx: 'auto' }}>

      {/* ── Header ── */}
      <Box sx={{
        mb: 3, p: 2.5, borderRadius: 3,
        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
        color: 'white', display: 'flex', alignItems: 'center', gap: 2,
      }}>
        <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ShowChart sx={{ fontSize: 24 }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={800} lineHeight={1.1}>Momentum Board</Typography>
          <Typography variant="body2" sx={{ opacity: 0.88, mt: 0.25 }}>
            30-day CVR momentum — who's surging, who's fading, and where the market is inefficient
            {meta && <span> · {meta.totalMovers} players tracked</span>}
          </Typography>
        </Box>
        <PageYearSelector />
      </Box>

      {/* ── Controls ── */}
      <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
        <ToggleButtonGroup value={playerType} exclusive size="small"
          onChange={(_, v) => { if (v) setPlayerType(v); }}>
          <ToggleButton value="Batter"  sx={{ px: 2, fontWeight: 700 }}>Batters</ToggleButton>
          <ToggleButton value="Pitcher" sx={{ px: 2, fontWeight: 700 }}>Pitchers</ToggleButton>
        </ToggleButtonGroup>
        <TextField size="small" placeholder="Search player…" value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <Search sx={{ mr: 0.5, color: 'text.secondary', fontSize: 18 }} /> }}
          sx={{ width: 200 }} />
      </Box>

      {/* ── Scatter chart ── */}
      {!isMobile && (
        <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.8)}`, mb: 3, p: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
            CVR Momentum Map — 30-Day Window
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            X = Current rolling CVR · Y = Change from 30 days ago · Top-right = best opportunity
          </Typography>
          {loading
            ? <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 2 }} />
            : (
              <ResponsiveContainer width="100%" height={280}>
                <ScatterChart margin={{ top: 10, right: 20, bottom: 10, left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
                  <XAxis
                    dataKey="x" type="number" domain={[0, 100]}
                    label={{ value: 'CVR Now', position: 'insideBottom', offset: -4, fontSize: 11, fill: theme.palette.text.secondary }}
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis
                    dataKey="y" type="number"
                    label={{ value: '30d Δ', angle: -90, position: 'insideLeft', offset: 10, fontSize: 11, fill: theme.palette.text.secondary }}
                    tick={{ fontSize: 11 }}
                  />
                  {/* Reference lines for quadrant boundaries */}
                  <ReferenceLine x={65} stroke={alpha(theme.palette.primary.main, 0.4)} strokeDasharray="4 4" />
                  <ReferenceLine y={0}  stroke={alpha(theme.palette.divider, 0.8)} />
                  <ReferenceLine y={7}  stroke={alpha('#16a34a', 0.3)} strokeDasharray="2 4" />
                  <ReferenceLine y={-5} stroke={alpha('#dc2626', 0.3)} strokeDasharray="2 4" />
                  <RTooltip content={<ScatterTooltip />} />
                  <Scatter data={scatterData} r={5}>
                    {scatterData.map((entry, i) => (
                      <Cell
                        key={i}
                        fill={entry.quadrant ? alpha(entry.quadrant.color, 0.7) : alpha(theme.palette.text.secondary, 0.3)}
                        stroke={entry.quadrant ? entry.quadrant.color : 'none'}
                        strokeWidth={1}
                      />
                    ))}
                  </Scatter>
                </ScatterChart>
              </ResponsiveContainer>
            )}
          {/* Quadrant legend */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1 }}>
            {QUADRANTS.map(q => (
              <Box key={q.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: q.color }} />
                <Typography variant="caption" color="text.secondary">{q.label}</Typography>
              </Box>
            ))}
          </Box>
        </Card>
      )}

      {/* ── 4 Quadrant cards ── */}
      {loading
        ? (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {QUADRANTS.map(q => (
              <Grid item xs={12} sm={6} key={q.id}>
                <Skeleton variant="rectangular" height={240} sx={{ borderRadius: 3 }} />
              </Grid>
            ))}
          </Grid>
        )
        : (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {QUADRANTS.map(q => (
              <Grid item xs={12} sm={6} key={q.id}>
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: QUADRANTS.indexOf(q) * 0.08 }}
                >
                  <QuadrantCard
                    q={q}
                    players={byQuadrant[q.id] || []}
                    navigate={navigate}
                    theme={theme}
                  />
                </motion.div>
              </Grid>
            ))}
          </Grid>
        )}

      {/* ── Full sorted list ── */}
      <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.8)}` }}>
        <Box sx={{ p: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}>
          <Typography variant="subtitle2" fontWeight={700}>
            Full Momentum Leaderboard
            {!loading && <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              {filtered.length} players · ranked by momentum score
            </Typography>}
          </Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                <TableCell sx={{ ...TH, width: 36 }}>#</TableCell>
                <TableCell sx={TH}>Player</TableCell>
                <TableCell sx={{ ...TH, textAlign: 'center' }}>CVR Now</TableCell>
                <TableCell sx={{ ...TH, textAlign: 'center' }}>30d Δ</TableCell>
                <TableCell sx={{ ...TH, textAlign: 'center' }}>Baseline</TableCell>
                <TableCell sx={{ ...TH, textAlign: 'center' }}>Score</TableCell>
                <TableCell sx={{ ...TH, textAlign: 'center' }}>Signal</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading
                ? Array(10).fill(0).map((_, i) => (
                    <TableRow key={i}>
                      {Array(7).fill(0).map((__, j) => <TableCell key={j}><Skeleton width={j === 1 ? 120 : 48} /></TableCell>)}
                    </TableRow>
                  ))
                : filtered.length === 0
                ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        {search ? 'No players match your search' : 'No momentum data available — snapshots populate nightly'}
                      </TableCell>
                    </TableRow>
                  )
                : filtered.map((p, i) => (
                    <MoverRow key={p.compositeId} rank={i + 1} player={p} navigate={navigate} theme={theme} />
                  ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
};

export default MomentumBoard;
